import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, FileText, Download } from 'lucide-react';
import { documentoAdminService, documentoPublicoService } from '../../api/services';
import { Button, Input, Select, Badge, Modal, Paginacao, Loading, Vazio } from '../../components/ui';
import { CATEGORIAS_DOCUMENTO } from '../../types';
import type { Documento, CategoriaDocumento } from '../../types';
import { formatarTamanho, formatarData, labelCategoria } from '../../utils/formatters';
import toast from 'react-hot-toast';
import './admin-paginas.css';

export default function AdminDocumentosPage() {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [modalNovoAberto, setModalNovoAberto] = useState(false);
  const [docEditando, setDocEditando] = useState<Documento | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await documentoAdminService.listar({ pagina, limite: 15, categoria: categoria || undefined, busca: busca || undefined });
      setDocumentos(r.documentos);
      setTotalPaginas(r.totalPaginas);
    } catch { toast.error('Erro ao carregar documentos'); }
    finally { setCarregando(false); }
  }, [pagina, categoria, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleDeletar = async (doc: Documento) => {
    if (!window.confirm(`Excluir "${doc.titulo}"?`)) return;
    try {
      await documentoAdminService.deletar(doc.id);
      toast.success('Documento excluído');
      carregar();
    } catch { toast.error('Erro ao excluir'); }
  };

  return (
    <div className="admin-pagina">
      <div className="admin-pagina-header">
        <div>
          <h1>Documentos</h1>
          <p>Gerencie os documentos públicos do portal de transparência</p>
        </div>
        <Button icone={<Plus size={16} />} onClick={() => setModalNovoAberto(true)}>
          Novo Documento
        </Button>
      </div>

      {/* Filtros */}
      <div className="admin-filtros">
        <Input
          placeholder="Buscar por título..."
          value={busca}
          onChange={e => { setBusca(e.target.value); setPagina(1); }}
          icone={<Search size={16} />}
        />
        <Select
          opcoes={CATEGORIAS_DOCUMENTO}
          value={categoria}
          onChange={e => { setCategoria(e.target.value); setPagina(1); }}
          placeholder="Todas as categorias"
        />
      </div>

      {/* Tabela */}
      {carregando ? <Loading /> : documentos.length === 0 ? (
        <Vazio icone={<FileText size={48} />} titulo="Nenhum documento" descricao="Clique em 'Novo Documento' para adicionar." />
      ) : (
        <>
          <div className="admin-tabela">
            <table>
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Categoria</th>
                  <th>Status</th>
                  <th>Tamanho</th>
                  <th>Data</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {documentos.map(doc => (
                  <tr key={doc.id}>
                    <td>
                      <div className="td-doc">
                        <FileText size={18} />
                        <div>
                          <strong>{doc.titulo}</strong>
                          {doc.nota && <span>{doc.nota}</span>}
                        </div>
                      </div>
                    </td>
                    <td><Badge texto={labelCategoria(doc.categoria)} cor="azul" /></td>
                    <td>
                      <Badge
                        texto={doc.status}
                        cor={doc.status === 'ativo' ? 'verde' : doc.status === 'arquivado' ? 'dourado' : 'cinza'}
                      />
                    </td>
                    <td className="td-small">{formatarTamanho(doc.tamanhoArquivo)}</td>
                    <td className="td-small">{formatarData(doc.criadoEm)}</td>
                    <td>
                      <div className="td-acoes">
                        {doc.urlPublica && (
                          <button
                            className="btn-acao"
                            title="Baixar"
                            onClick={() => window.open(documentoPublicoService.downloadUrl(doc.id), '_blank')}
                          >
                            <Download size={15} />
                          </button>
                        )}
                        <button className="btn-acao" title="Editar" onClick={() => setDocEditando(doc)}>
                          <Pencil size={15} />
                        </button>
                        <button className="btn-acao perigo" title="Excluir" onClick={() => handleDeletar(doc)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudar={setPagina} />
        </>
      )}

      {/* Modal Novo */}
      <Modal aberto={modalNovoAberto} onFechar={() => setModalNovoAberto(false)} titulo="Novo Documento" largura="md">
        <FormDocumento onSalvar={() => { setModalNovoAberto(false); carregar(); }} />
      </Modal>

      {/* Modal Editar */}
      <Modal aberto={!!docEditando} onFechar={() => setDocEditando(null)} titulo="Editar Documento" largura="md">
        {docEditando && (
          <FormEditarDocumento
            doc={docEditando}
            onSalvar={() => { setDocEditando(null); carregar(); }}
          />
        )}
      </Modal>
    </div>
  );
}

// ===== FORM NOVO =====
function FormDocumento({ onSalvar }: { onSalvar: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState<CategoriaDocumento>('ata');
  const [nota, setNota] = useState('');
  const [data, setData] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    if (!titulo || !arquivo) { toast.error('Título e arquivo são obrigatórios'); return; }
    setSalvando(true);
    try {
      const fd = new FormData();
      fd.append('titulo', titulo);
      fd.append('categoria', categoria);
      if (nota) fd.append('nota', nota);
      if (data) fd.append('data', data);
      fd.append('arquivo', arquivo);
      await documentoAdminService.criar(fd);
      toast.success('Documento publicado!');
      onSalvar();
    } catch (e: any) {
      toast.error(e?.response?.data?.erro || 'Erro ao salvar');
    } finally { setSalvando(false); }
  };

  return (
    <div className="form-stack">
      <Input label="Título do documento" value={titulo} onChange={e => setTitulo(e.target.value)} required placeholder="Ex: Ata da Assembleia Geral 2024" />
      <Select label="Categoria" opcoes={CATEGORIAS_DOCUMENTO} value={categoria} onChange={e => setCategoria(e.target.value as CategoriaDocumento)} />
      <div className="campo-grupo">
        <label className="campo-label">Data do documento</label>
        <input type="date" className="campo-input" value={data} onChange={e => setData(e.target.value)} />
      </div>
      <div className="campo-grupo">
        <label className="campo-label">Observação (opcional)</label>
        <textarea className="campo-input" rows={3} value={nota} onChange={e => setNota(e.target.value)} placeholder="Breve descrição do documento..." />
      </div>
      <div className="campo-grupo">
        <label className="campo-label">Arquivo (PDF, DOC, etc.)</label>
        <input type="file" className="campo-input" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg" onChange={e => setArquivo(e.target.files?.[0] || null)} />
        {arquivo && <span style={{ fontSize: '0.8rem', color: 'var(--cinza)' }}>{arquivo.name} — {formatarTamanho(arquivo.size)}</span>}
      </div>
      <div className="form-acoes">
        <Button variante="primario" carregando={salvando} onClick={handleSalvar}>Publicar Documento</Button>
      </div>
    </div>
  );
}

// ===== FORM EDITAR =====
function FormEditarDocumento({ doc, onSalvar }: { doc: Documento; onSalvar: () => void }) {
  const [titulo, setTitulo] = useState(doc.titulo);
  const [categoria, setCategoria] = useState(doc.categoria);
  const [nota, setNota] = useState(doc.nota || '');
  const [status, setStatus] = useState(doc.status);
  const [salvando, setSalvando] = useState(false);

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      await documentoAdminService.atualizar(doc.id, { titulo, categoria, nota, status });
      toast.success('Documento atualizado!');
      onSalvar();
    } catch { toast.error('Erro ao atualizar'); }
    finally { setSalvando(false); }
  };

  return (
    <div className="form-stack">
      <Input label="Título" value={titulo} onChange={e => setTitulo(e.target.value)} />
      <Select label="Categoria" opcoes={CATEGORIAS_DOCUMENTO} value={categoria} onChange={e => setCategoria(e.target.value as CategoriaDocumento)} />
      <Select label="Status" opcoes={[{ value: 'ativo', label: 'Ativo' }, { value: 'arquivado', label: 'Arquivado' }, { value: 'inativo', label: 'Inativo' }]} value={status} onChange={e => setStatus(e.target.value as any)} />
      <div className="campo-grupo">
        <label className="campo-label">Observação</label>
        <textarea className="campo-input" rows={3} value={nota} onChange={e => setNota(e.target.value)} />
      </div>
      <div className="form-acoes">
        <Button variante="primario" carregando={salvando} onClick={handleSalvar}>Salvar Alterações</Button>
      </div>
    </div>
  );
}
